import { create } from "zustand";
import { api } from "../lib/api";
import { useAIStore } from "./ai";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GCMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
}

export interface GCConversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  forked_from_id?: string | null;
  forked_from_title?: string | null;
}

export interface MemoryItem {
  id: string;
  content: string;
  source?: string;
  source_id?: string;
  tags?: string[];
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

type TimeGroup = "today" | "yesterday" | "previous7" | "thisMonth" | "older";

export interface GroupedConversations {
  label: string;
  key: TimeGroup;
  items: GCConversation[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByTime(convs: GCConversation[]): GroupedConversations[] {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86_400_000);
  const weekStart = new Date(todayStart.getTime() - 7 * 86_400_000);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const buckets: Record<TimeGroup, GCConversation[]> = {
    today: [],
    yesterday: [],
    previous7: [],
    thisMonth: [],
    older: [],
  };

  for (const c of convs) {
    const d = new Date(c.updated_at);
    if (d >= todayStart) buckets.today.push(c);
    else if (d >= yesterdayStart) buckets.yesterday.push(c);
    else if (d >= weekStart) buckets.previous7.push(c);
    else if (d >= monthStart) buckets.thisMonth.push(c);
    else buckets.older.push(c);
  }

  const labels: Record<TimeGroup, string> = {
    today: "Today",
    yesterday: "Yesterday",
    previous7: "Previous 7 days",
    thisMonth: "This month",
    older: "Older",
  };

  return (Object.keys(buckets) as TimeGroup[])
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ label: labels[k], key: k, items: buckets[k] }));
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface GlobalChatState {
  // Conversations
  conversations: GCConversation[];
  conversationsLoaded: boolean;
  currentConversationId: string | null;
  messages: GCMessage[];

  // Streaming
  isStreaming: boolean;
  _streamAbort: AbortController | null;

  // Memory
  memoryItems: MemoryItem[];
  memoryLoaded: boolean;

  // UI
  sidebarOpen: boolean;
  memoryPanelOpen: boolean;
  searchQuery: string;

  // Computed
  groupedConversations: () => GroupedConversations[];
  filteredGrouped: () => GroupedConversations[];

  // Conversation actions
  fetchConversations: () => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  startNewConversation: () => void;
  stopStreaming: () => void;
  forkConversation: (messageId: string) => Promise<void>;
  navigateToParent: () => void;

  // Memory actions
  fetchMemory: () => Promise<void>;
  saveToMemory: (content: string, sourceId?: string) => Promise<void>;
  deleteMemory: (id: string) => Promise<void>;
  togglePinMemory: (id: string) => Promise<void>;

  // UI actions
  setSidebarOpen: (open: boolean) => void;
  setMemoryPanelOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
}

export const useGlobalChatStore = create<GlobalChatState>()((set, get) => ({
  conversations: [],
  conversationsLoaded: false,
  currentConversationId: null,
  messages: [],

  isStreaming: false,
  _streamAbort: null,

  memoryItems: [],
  memoryLoaded: false,

  sidebarOpen: true,
  memoryPanelOpen: false,
  searchQuery: "",

  // ── Computed ──

  groupedConversations: () => groupByTime(get().conversations),

  filteredGrouped: () => {
    const q = get().searchQuery.toLowerCase().trim();
    if (!q) return groupByTime(get().conversations);
    const filtered = get().conversations.filter((c) =>
      c.title.toLowerCase().includes(q),
    );
    return groupByTime(filtered);
  },

  // ── Conversation actions ──

  fetchConversations: async () => {
    try {
      const data = await api.get<GCConversation[]>(
        "/api/v1/chat/conversations?limit=50",
      );
      set({ conversations: data, conversationsLoaded: true });
    } catch {
      set({ conversationsLoaded: true });
    }
  },

  loadConversation: async (id: string) => {
    set({ currentConversationId: id, messages: [] });
    try {
      const data = await api.get<{
        messages: GCMessage[];
        forked_from_id?: string | null;
        title?: string;
      }>(`/api/v1/conversations/${id}`);
      set((s) => {
        const updates: Partial<GlobalChatState> = { messages: data.messages || [] };
        if (data.forked_from_id) {
          const parentConv = s.conversations.find((c) => c.id === data.forked_from_id);
          updates.conversations = s.conversations.map((c) =>
            c.id === id
              ? { ...c, forked_from_id: data.forked_from_id, forked_from_title: parentConv?.title ?? null }
              : c,
          );
        }
        return updates;
      });

      if (data.forked_from_id) {
        const parentConv = get().conversations.find((c) => c.id === data.forked_from_id);
        if (!parentConv) {
          try {
            const parent = await api.get<{ title?: string }>(`/api/v1/conversations/${data.forked_from_id}`);
            set((s) => ({
              conversations: s.conversations.map((c) =>
                c.id === id ? { ...c, forked_from_title: parent.title ?? "Untitled" } : c,
              ),
            }));
          } catch {
            // Parent may have been deleted
          }
        }
      }
    } catch {
      set({ messages: [] });
    }
  },

  sendMessage: async (content: string) => {
    const { isStreaming, currentConversationId } = get();
    if (isStreaming) return;

    const userMsg: GCMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content,
    };
    const assistantMsg: GCMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
    };

    const controller = new AbortController();
    set((s) => ({
      messages: [...s.messages, userMsg, assistantMsg],
      isStreaming: true,
      _streamAbort: controller,
    }));

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000"}/api/v1/chat/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${api.getToken() || ""}`,
          },
          body: JSON.stringify({
            content,
            conversation_id: currentConversationId,
            provider: useAIStore.getState().selectedProvider || undefined,
            model: useAIStore.getState().selectedModel || undefined,
          }),
          signal: controller.signal,
        },
      );

      if (!response.ok) throw new Error("Chat request failed");
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.chunk) {
              set((s) => {
                const msgs = [...s.messages];
                const last = msgs[msgs.length - 1];
                if (last?.role === "assistant") {
                  msgs[msgs.length - 1] = {
                    ...last,
                    content: last.content + parsed.chunk,
                  };
                }
                return { messages: msgs };
              });
            }
            if (parsed.conversation_id && !get().currentConversationId) {
              set({ currentConversationId: parsed.conversation_id });
              // Refresh conversation list to include the new one
              get().fetchConversations();
            }
          } catch {
            // skip malformed chunks
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // User stopped the stream — keep partial content
        return;
      }
      // Remove the empty assistant message on error
      set((s) => ({
        messages: s.messages.filter((m) => m.id !== assistantMsg.id),
      }));
    } finally {
      set({ isStreaming: false, _streamAbort: null });
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await api.delete(`/api/v1/conversations/${id}`);
      set((s) => ({
        conversations: s.conversations.filter((c) => c.id !== id),
        ...(s.currentConversationId === id
          ? { currentConversationId: null, messages: [] }
          : {}),
      }));
    } catch {
      // silently fail
    }
  },

  startNewConversation: () => {
    get().stopStreaming();
    set({ currentConversationId: null, messages: [] });
  },

  stopStreaming: () => {
    const { _streamAbort } = get();
    if (_streamAbort) {
      _streamAbort.abort();
      set({ isStreaming: false, _streamAbort: null });
    }
  },

  forkConversation: async (messageId: string) => {
    const { currentConversationId } = get();
    if (!currentConversationId) return;

    try {
      const result = await api.post<{
        id: string;
        title: string;
        created_at: string;
        updated_at: string;
        forked_from_id: string;
        forked_from_title: string;
        messages: GCMessage[];
      }>(`/api/v1/conversations/${currentConversationId}/fork`, {
        message_id: messageId,
      });

      const newConv: GCConversation = {
        id: result.id,
        title: result.title,
        created_at: result.created_at,
        updated_at: result.updated_at,
        forked_from_id: result.forked_from_id,
        forked_from_title: result.forked_from_title,
      };

      set((s) => ({
        conversations: [newConv, ...s.conversations],
        currentConversationId: result.id,
        messages: result.messages || [],
      }));
    } catch {
      throw new Error("Failed to fork conversation");
    }
  },

  navigateToParent: () => {
    const { conversations, currentConversationId } = get();
    if (!currentConversationId) return;
    const current = conversations.find((c) => c.id === currentConversationId);
    if (!current?.forked_from_id) return;
    get().loadConversation(current.forked_from_id);
  },

  // ── Memory actions ──

  fetchMemory: async () => {
    try {
      const data = await api.get<MemoryItem[]>("/api/v1/memory");
      set({ memoryItems: data, memoryLoaded: true });
    } catch {
      set({ memoryLoaded: true });
    }
  },

  saveToMemory: async (content: string, sourceId?: string) => {
    try {
      const item = await api.post<MemoryItem>("/api/v1/memory", {
        content,
        source: "conversation",
        source_id: sourceId,
      });
      set((s) => ({ memoryItems: [item, ...s.memoryItems] }));
    } catch {
      throw new Error("Failed to save memory");
    }
  },

  deleteMemory: async (id: string) => {
    try {
      await api.delete(`/api/v1/memory/${id}`);
      set((s) => ({
        memoryItems: s.memoryItems.filter((m) => m.id !== id),
      }));
    } catch {
      // silently fail
    }
  },

  togglePinMemory: async (id: string) => {
    const item = get().memoryItems.find((m) => m.id === id);
    if (!item) return;
    try {
      await api.put(`/api/v1/memory/${id}`, { pinned: !item.pinned });
      set((s) => ({
        memoryItems: s.memoryItems.map((m) =>
          m.id === id ? { ...m, pinned: !m.pinned } : m,
        ),
      }));
    } catch {
      // silently fail
    }
  },

  // ── UI actions ──

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setMemoryPanelOpen: (open) => {
    set({ memoryPanelOpen: open });
    if (open && !get().memoryLoaded) get().fetchMemory();
  },
  setSearchQuery: (query) => set({ searchQuery: query }),
}));
